
import { useEffect, useState } from "react";
import { GET } from "../service/axiosMethods";

type ServiceStatus = "UP" | "DOWN" | "UNKNOWN";

interface SystemHealthResponse {
  gateway: { status: ServiceStatus };
  services: {
    [name: string]: {
      status: ServiceStatus;
      details?: any;
      error?: string;
    };
  };
}

interface ServiceHealthState {
  gatewayStatus: ServiceStatus;
  downServices: string[];
  hasIssue: boolean;
  loading: boolean;
  error?: string;
}

export function useServiceHealthStatus(enabled: boolean, pollIntervalMs = 15000): ServiceHealthState {
  const [state, setState] = useState<ServiceHealthState>({
    gatewayStatus: "UNKNOWN",
    downServices: [],
    hasIssue: false,
    loading: true,
  });

  useEffect(() => {
    if (!enabled) {
      setState((prev) => ({
        ...prev,
        loading: false,
      }));
      return;
    }

    let cancelled = false;

    const checkHealth = async () => {
      try {
        const res = await GET("/system/health"); // Axios wrapper
        const data = res.data as SystemHealthResponse;

        const gatewayStatus = data.gateway?.status ?? "UNKNOWN";

        const downServices = Object.entries(data.services || {})
          .filter(([, info]) => info.status === "DOWN")
          .map(([name]) => name);

        if (cancelled) return;

        setState({
          gatewayStatus,
          downServices,
          hasIssue: gatewayStatus !== "UP" || downServices.length > 0,
          loading: false,
          error: undefined,
        });
      } catch (e: any) {
        if (cancelled) return;

        setState({
          gatewayStatus: "DOWN",
          downServices: [],
          hasIssue: true, // if /system/health itself fails, things are bad
          loading: false,
          error: e?.message || "Failed to check health",
        });
      }
    };

    checkHealth();
    const id = window.setInterval(checkHealth, pollIntervalMs);

    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [enabled, pollIntervalMs]);

  return state;
}
